import _ from 'underscore';
import Backbone from 'backbone';
import Localizer from 'localizer';
import Personalizer from 'personalizer';
import LocalCache from 'utils/sync/LocalCache'; // Despite this not being used directly, removing it here causes 404's
import viewerUserConnections from 'collections/connections/ViewerUserConnections';
import timeZones from 'collections/activity/TimeZones';
import UserPreference from 'models/user/UserPreference';
import UserPreferences from 'models/user/UserPreferences';
import UserSettings from 'models/user/UserSettings';
import ServiceHost from 'models/system/ServiceHost';
import NewsFeedStatus from 'models/connections/NewsFeedStatus';
import viewerSocialProfile from 'models/user/ViewerSocialProfile';
import viewerUserBiometrics from 'models/user/ViewerUserBiometrics';
import viewerUserPulseOxEnable from 'models/user/ViewerPulseOxEnable';
import viewerUserPreferences from 'models/user/ViewerUserPreferences';
import UsageIndicators from 'models/indicators/UsageIndicators';
import viewerAgentDevices from 'models/device/ViewerAgentDevices';
import ModelSynchronizer from 'utils/ModelSynchronizer';
import { showMenstrualCycleTrackingWithoutMCTSettingsData, isMctFeatureEnabled } from 'utils/MctUtil';
import SystemPreferences from 'models/system/SystemPreferences';
import Preferences from 'models/preferences/Preferences';
import GdprFeature from 'models/gdpr/Feature';
import GdprUtil from 'utils/gdpr/GdprUtil';
import Insights from 'collections/insights/Insights';
import TrainingPlans from 'collections/training_plan/TrainingPlans';
import {
    ecgRoleKey,
    gc5StressPageRoleKey,
    dswRoleKey, // Clean up ticket: CA-161362
    gc5HydrationPageRoleKey, // Cleanup ticket: CA-159279
    groupInviteSpammingDaysKey,
    gcPlusFrameRoleKey, // Clean up ticket: CA-165763
    harmonyConnectAppRoleKey,
    runningEconomyRoleKey,
    runningToleranceRoleKey,
    garminTrailsRoleKey // Cleanup ticket: CA-191687
} from 'models/preferences/PreferencesMapping';
import { shouldRestrictByRole } from 'utils/WindowPreferencesMappings';
import Loadable from 'utils/extensions/Loadable';
import BetaTestersSurveyBanner from 'views/main/BetaTestersSurveyBanner';
import MainSidebarView from 'views/main/MainContainer/MainSidebarView';
import TopHeaderBarView from 'views/main/MainContainer/TopHeaderBarView';
import ReactComponentView from 'views/main/react/ReactComponentView';
import Preference from 'models/preferences/Preferences';
import SystemPreference from 'models/system/SystemPreference';
import PrivacyView from 'views/gdpr/privacy/PrivacyView';
import ApiVersion from 'models/system/ApiVersion';
import { isMale } from 'utils/UserUtil';
import { CYCLE_TYPE_PREGNANT } from 'constants/MenstrualCycleTrackingConstants';
import Pregnancysnapshots from 'collections/user/periodichealth/Pregnancysnapshots';
import { isPregnancyDay } from 'utils/PregnancyUtil';
import { ecgPreferenceKey } from 'utils/ecg/ecgConstants';
import { showEcgInBackbone } from 'utils/ecg/ecgUtil';
import { applyHarmonyConnectCustomization } from 'utils/harmonyConnectApp/harmonyConnectAppUtil.js';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import { isReadonlyMode, isRaceManagementMode } from 'system/SystemUtil';
import SnackbarMessageAction from 'system/SnackbarMessageAction';
import UserBiometrics from 'models/user/ViewerUserBiometrics';
import GlobalModal from 'standaloneComponents/GlobalModal';
import Header from 'standaloneComponents/Header';
import SnackbarMessageWrapper from 'standaloneComponents/SnackbarMessageWrapper';
import Consent from 'standaloneComponents/Consent/Consent';
import profileImageHelperV2 from 'utils/userSocialProfileImage/profileImageHelperV2';
import MyFitnessPalAuthorization from 'models/calories/MyFitnessPalAuthorization';
import MainSidebar from 'components/MainSidebar/MainSidebarWrapper';

Backbone.View = Backbone.View.extend({
    /**
     * Extend all views to have this function which allows parsing of the query string.  We might want to consider
     *  caching the results of this call so it doesn't have to be parsed every time but I wouldn't expect it to be
     *  called too frequently.
     *
     */
    getUrlVars: function() {
        let vars = {};
        window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, function(m, key, value) {
            vars[key] = value;
        });
        return vars;
    }
});

let QUEUE_POLL_RATE = 60000; //1 minute
let Events = {
    UNAUTHENTICATED_RENDER_COMPLETE: 'UNAUTHENTICATED_RENDER_COMPLETE'
};

let LoadableView = Backbone.View.extend(Loadable);

let MainContainerView = LoadableView.extend({
    propertyFiles: [
        '/main_container/main_container',
        '/widgets/widgets',
        '/common/common',
        '/validation/validation',
        '/userprofile/userprofile',
        '/send_to_device/send_to_device',
        '/activity-sync/activity-sync',
        '/group/group',
        '/challengewidget/challengewidget',
        '/connections/connections',
        '/activity_types/activity_types',
        '/units/units',
        '/weightwidget/weightwidget',
        '/widgets/widgets',
        '/course/course',
        '/map/map',
        '/trophy_case/trophy_case',
        '/training_plans/training_plans',
        '/goals/goals',
        '/device/device',
        '/personal-record-api/personal-record-api',
        '/daily_summary/daily_summary',
        '/golf_main/golf_main',
        '/event_types/event_types',
        '/workout/workout',
        '/gear_tracking/gear_tracking',
        '/badges-list/badges-list',
        '/badges/badges',
        '/notification/notification',
        '/bodybattery/bodybattery',
        '/insights/insights',
        '/hydration/hydration',
        '/activity-api/activity-api',
        '/menstrual-cycle-tracking/menstrual-cycle-tracking',
        '/settings/settings',
        '/fitness_age/fitness_age',
        '/bloodPressure/bloodPressure',
        '/health_snapshot/health_snapshot',
        '/power_guidance/power_guidance',
        '/ecg/ecg',
        '/news_feed/news_feed',
        '/solar_charge/solar_charge',
        '/jet_lag/jet_lag',
        '/account_sharing/account_sharing',
        '/trails/trails',
        '/performance_dashboard/performance_dashboard'
    ],

    events: {
        'click .connect-header-link': 'onConnectHeaderClicked'
    },

    initialize: function(args) {
        this.contextPath = args.contextPath;
        this.viewerIsAuthenticated = args.viewerIsAuthenticated;
        this.userProfile = args.userProfile;
        this.userPreferences = args.userPreferences;
        this.serviceHost = new ServiceHost();
        this.menuItems = [];
        this.isGCPEnabled = false;

        this.navCollapsedPref = new UserPreference({
            key: 'MainNavigation.collapsed'
        });

        this.leafletEnabledPref = new UserPreference({
            key: 'Web.MapView.LeafletEnabled'
        });

        this.internalAppsHost = new Preferences({
            key: 'internal-apps-host'
        });

        this.caloriesAvailablePref = new UserPreference({
            key: 'DashboardService.EnableCalorieWidget'
        });

        this.ecgRolePreference = new Preference({
            key: ecgRoleKey
        });

        this.ecgEnabled = new UserPreference({
            key: ecgPreferenceKey
        });

        this.gdprFeatureUpload = new GdprFeature({
            feature: GdprFeature.Features.UPLOAD
        });

        if (!args.viewerIsAuthenticated) {
            this.viewerIsAuthenticated = false;
        }

        this.isError = args.isError;

        if (!this.contextPath) {
            this.contextPath = '/';
        }

        this.dsPage = new Preference({ key: 'ds-routing' });

        this.userSettings = new UserSettings();

        this.gc5StressPageRole = new Preference({ key: gc5StressPageRoleKey });
        this.gc5HydrationPageRole = new Preference({ key: gc5HydrationPageRoleKey }); // Cleanup ticket: CA-159279
        this.groupInviteSpammingPref = new Preference({ key: groupInviteSpammingDaysKey });
        this.gcPlusFrameRole = new Preference({ key: gcPlusFrameRoleKey }); // Clean up ticket: CA-165763
        this.harmonyConnectAppRole = new Preference({ key: harmonyConnectAppRoleKey }); // Cleanup ticket: CA-191687

        this.insightsGDPRConsent = new GdprFeature({ feature: GdprFeature.Features.INSIGHTS });
        this.insightsHistory = new Insights();

        this.messageAndDevicesNotificationsEnabled = new SystemPreference({
            preferenceKey: 'WebConstants.queue.notification.enabled'
        });

        this.usageIndicators = new UsageIndicators();

        this.newsFeedStatus = new NewsFeedStatus({
            lastViewedTimeGMT: this.viewerIsAuthenticated
                ? viewerUserPreferences.getNewsFeedLastReadTimeFromCookies()
                : new Date().toUTCString()
        });

        this.userBiometrics = UserBiometrics;

        this.apiVersion = new ApiVersion();

        this.forceReloadSidebar = false;
        this.mfp = new MyFitnessPalAuthorization();

        // Clean up ticket: CA-161362
        this.dswRole = new Preference({ key: dswRoleKey });
        // Cleanup ticket: CA-193107
        this.runningEconomy = new Preference({ key: runningEconomyRoleKey });
        // Clean up ticket: CA-185063
        this.runningTolerance = new Preference({ key: runningToleranceRoleKey });
        // Cleanup ticket: CA-159335
        this.trail = new Preference({ key: garminTrailsRoleKey });

        window.onmessage = (e) => {
            if (e.data === 'RELOAD_NAVIGATION') {
                (() => {
                    this.reloadSidebar();
                })();
            }
        };

        this.trainingPlans = new TrainingPlans({});
    },

    showPrivacyOverlay: function() {
        if (!window.ERROR_VIEW) {
            let privacyView = new PrivacyView({
                el: $('.privacy-overlay')
            });

            privacyView.render();
        } else {
            $('.privacy-overlay').remove();
        }
    },

    onSessionReauthenticateStart: function() {
        this.showWaitCursor();
    },

    onSessionReathenticateFinish: function() {
        this.hideWaitCursor();
    },

    showWaitCursor: function() {
        jQuery('body').addClass('wait');
    },

    hideWaitCursor: function() {
        jQuery('body').removeClass('wait');
    },

    onLanguageSelected: function(locale) {
        UserPreferences.writeLegacyLocaleCookie(locale);
        UserPreferences.updateLegacySessionLocale(locale);
        window.location.reload();
    },

    provideDependencies: function(modelSynchronizer) {
        // If user isn't authenticated, container doesn't do much.
        if (!this.viewerIsAuthenticated) {
            return;
        }

        // This should be loaded for unauthenticated viewers, but can't be due to 500 error
        modelSynchronizer.addModel({
            model: timeZones,
            required: true,
            countError: true
        });
        modelSynchronizer.addEvent({
            model: Localizer,
            event: Localizer.Events.ON_LOCAL_LOADED,
            required: true
        });
        modelSynchronizer.addModel({ model: this.serviceHost, required: true });

        // No connections info in Account Sharing
        if (!isReadonlyMode()) {
            modelSynchronizer.addModel({
                model: viewerUserConnections,
                required: true
            });
        }
        modelSynchronizer.addModel({ model: viewerUserBiometrics, required: true });
        modelSynchronizer.addModel({
            model: viewerUserPulseOxEnable,
            required: true
        });
        modelSynchronizer.addModel({
            model: this.caloriesAvailablePref,
            required: true
        });
        modelSynchronizer.addModel({
            model: this.navCollapsedPref,
            required: true
        });
        modelSynchronizer.addModel({
            model: this.leafletEnabledPref,
            required: true
        });
        modelSynchronizer.addModel({
            model: this.internalAppsHost,
            required: true
        });
        modelSynchronizer.addModel({
            model: this.dsPage,
            required: true
        });
        modelSynchronizer.addEvent({
            model: modelSynchronizer,
            event: ModelSynchronizer.Events.ON_DOCUMENT_READY,
            required: true
        });
        modelSynchronizer.addModel({
            model: this.userSettings,
            required: true,
            countError: true
        });

        modelSynchronizer.addModel({
            model: this.messageAndDevicesNotificationsEnabled,
            required: true
        });

        modelSynchronizer.addModel({
            model: this.gdprFeatureUpload,
            required: true
        });

        modelSynchronizer.addModel({
            model: this.ecgRolePreference,
            required: true
        });

        if (!shouldRestrictByRole(ecgRoleKey, viewerSocialProfile.get('userRoles'))) {
            modelSynchronizer.addModel({
                model: this.ecgEnabled,
                required: true
            });
        }

        modelSynchronizer.addModel({
            model: this.usageIndicators,
            required: true
        });

        modelSynchronizer.addModel({ model: this.newsFeedStatus, required: true });

        if (viewerSocialProfile.isWellnessUser()) {
            modelSynchronizer.addModel({ model: viewerAgentDevices, required: true });
        }
        modelSynchronizer.addModel({
            model: this.groupInviteSpammingPref,
            required: true
        });

        modelSynchronizer.addModel({
            model: this.insightsGDPRConsent,
            required: true
        });

        modelSynchronizer.addModel({
            model: this.gc5StressPageRole,
            required: true
        });
        // Clean up ticket: CA-161362
        modelSynchronizer.addModel({
            model: this.dswRole,
            required: true
        });
        modelSynchronizer.addModel({
            model: this.gc5HydrationPageRole, // Cleanup ticket: CA-159279
            required: true
        });

        modelSynchronizer.addModel({
            model: this.gcPlusFrameRole // Clean up ticket: CA-165763
        });

        modelSynchronizer.addModel({
            model: this.harmonyConnectAppRole, // Cleanup ticket: CA-191687
            required: true
        });

        modelSynchronizer.addModel({
            model: this.trainingPlans,
            required: true
        });

        // Cleanup ticket: CA-193107
        modelSynchronizer.addModel({
            model: this.runningEconomy,
            required: true
        });

        // Clean up ticket: CA-185063
        modelSynchronizer.addModel({
            model: this.runningTolerance,
            required: true
        });

        modelSynchronizer.addModel({
            model: this.mfp,
            required: false
        });

        // Clean up ticket: CA-185063
        modelSynchronizer.addModel({
            model: this.runningTolerance,
            required: true
        });

        // Cleanup ticket: CA-193107
        modelSynchronizer.addModel({
            model: this.runningEconomy,
            required: true
        });

        // Cleanup ticket: CA-159335
        modelSynchronizer.addModel({
            model: this.trail,
            required: true
        });
    },

    loadDependencies: function() {
        Localizer.setContextPath('/web-translations');
        Localizer.setLocale(this.model.getLocale());

        this.apiVersion.fetch();
    },

    /**
     * Called when all the prerequisites for being able to render this view have been met. This doesn't mean that
     * the child view is ready to render yet. That happens on onWidgetContainerLoaded.
     */
    onDependenciesLoaded: function() {
        // only golf user support this function
        if (viewerSocialProfile.isOutdoorUser() && this.userSettings.get('userData')) {
            Personalizer.setGolfMeasurementSystem(this.userSettings.get('userData').golfDistanceUnit);
        }
        SystemPreferences.leafletEnabled = this.leafletEnabledPref.get('value');
        let userSettingsJSON = this.userSettings.toJSON();

        let pwd = viewerAgentDevices.getPrimaryWearableDevice();
        if (pwd) {
            AnalyticsUtil.push({
                event: 'track_event',
                category: 'Device Usage Event',
                action: pwd.get('applicationKey'),
                label: pwd.get('applicationKey')
            });
        }

        let waitForRender = false;
        let modelSynchronizer = new ModelSynchronizer();
        if (!ERROR_VIEW && viewerSocialProfile.isUserLoggedIn() && !isMale(userSettingsJSON)) {
            // If the user isn't logged in, we can't get pregnancy information for them
            this.pregnancysnapshots = new Pregnancysnapshots();
            modelSynchronizer.addModel({ model: this.pregnancysnapshots, required: true });
            waitForRender = true;
        }

        const isWheelchairModeWithGDPR =
            this.userSettings.getWheelchairModeEnabled() && this.isGdprFeatureOn(this.insightsGDPRConsent);
        if (isWheelchairModeWithGDPR) {
            // insights history can be empty, which is error in model synchronizer
            // need to count error as well to ensure it does not block the page from rendering
            modelSynchronizer.addModel({ model: this.insightsHistory, required: true, countError: true });
            waitForRender = true;
        }

        if (waitForRender) {
            modelSynchronizer.bind(
                ModelSynchronizer.Events.SYNCHRONIZED,
                function() {
                    if (isWheelchairModeWithGDPR) {
                        this.menusSetup = false;
                        this.forceReloadSidebar = true;
                    }
                    this.render();
                },
                this
            );
            modelSynchronizer.fetchModels();
            return;
        }

        if (this.gcPlusFrameRole.featureEnabled()) {
            profileImageHelperV2.setDefaultImageVersion(2);
        }

        this.render();
    },

    render: function() {
        this.logAppVersion();

        this.showPrivacyOverlay();

        const isEmbed = window.location.href.indexOf('/embed/') !== -1;

        if (this.isError || !isEmbed) {
            this.setupReactModal();

            this.renderCookieConsentBanner();

            if (!this.viewerIsAuthenticated) {
                this.renderLoggedOutHeader();
                this.trigger(Events.UNAUTHENTICATED_RENDER_COMPLETE);
                return;
            }

            // The MainContainer view binds itself to existing DOM, so there is currently nothing to render for
            // itself. But we do need to render the menu and tabs.
            if (viewerSocialProfile.hasUserExperience()) {
                this.renderBetaTestersSurveyBanner();
            }

            this.setupMenus();

            // Used to keep the same behaviour as default select elements.
            $('.main-body').on('scroll.main', function() {
                $('.has-date-picker')
                    .datepicker('hide')
                    .blur()
                    .datepicker('widget')
                    .hide();

                let timepickers = $('.has-time-picker');
                if (timepickers.length > 0) {
                    timepickers.timepicker('hide').blur();
                }
            });

            if (applyHarmonyConnectCustomization(this.harmonyConnectAppRole.featureEnabled())) {
                // append the class so we can update the CSS to remove the extra space for header
                $('.main-body').addClass('harmony-connect-no-header');
            }
        }
    },

    renderSnackbarMessage: function() {
        this.snackbarMessage = new ReactComponentView();
        this.snackbarMessage.renderComponent(SnackbarMessageWrapper, jQuery('.header-snackbar')[0]);
    },

    renderBetaTestersSurveyBanner: function() {
        if (!this.betaTestersSurveyBanner) {
            this.betaTestersSurveyBanner = new BetaTestersSurveyBanner({
                el: jQuery('.beta-testers-survey-banner')
            });

            this.betaTestersSurveyBanner.load();
        }
    },

    /**
     * Called when the user clicks on the Garmin logo. Should go back to the dashboard.
     */
    onConnectHeaderClicked: function(e) {
        if (this.viewerIsAuthenticated) {
            e.preventDefault();
            Backbone.history.navigate('/', { trigger: true });
        }
        // else let EE handle the routing
    },

    setupMenus: function() {
        if (applyHarmonyConnectCustomization(this.harmonyConnectAppRole.featureEnabled())) {
            // Just skip the setup, mainly to prevent below two renders
            //   1. renderSidebar()
            //   2. renderHeaderBar()
            return;
        }

        //Our scripts are go
        jQuery('.no-script').removeClass('no-script');

        //Stop anchor from jumping
        jQuery('a[href="#"]').click(function(e) {
            e.preventDefault();
        });

        if (this.menusSetup) {
            console.log('SetupMenus: Menu has already been setup.');
        } else {
            this.menusSetup = true;

            this.renderSidebar();
            this.renderHeaderBar();
        }
    },

    renderLoggedOutHeader: function() {
        this.header = new ReactComponentView();
        this.header.renderComponent(Header, jQuery('.main-header')[0]);
    },

    renderSidebar: function() {
        if (!this.sidebar || this.forceReloadSidebar) {
            this.preferences = {}; // setup system preferences object - used to wrap menu items in preference conditionals,
            // so they can be turned off together with the feature page

            if (viewerSocialProfile.isFitnessUser() || viewerSocialProfile.isWellnessUser()) {
                this.preferences[
                    'hasPulseOxAllDayCapableDevice'
                ] = this.usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasPulseOxAllDayCapableDevice');
            }

            this.preferences['hasRespirationCapableDevice'] = this.usageIndicators.hasCurrentOrHistoricalIndicator(
                'device',
                'hasRespirationCapableDevice'
            );

            this.preferences['hasBodyBatteryCapableDevice'] = this.usageIndicators.hasCurrentOrHistoricalIndicator(
                'device',
                'hasBodyBatteryCapableDevice'
            );

            this.preferences['hasFitnessAgeV2CapableDevice'] = this.usageIndicators.hasCurrentOrHistoricalIndicator(
                'device',
                'hasFitnessAgeV2CapableDevice'
            );

            this.preferences['hasPowerGuidanceCapableDevice'] = this.usageIndicators.hasCurrentOrHistoricalIndicator(
                'device',
                'hasPowerGuidanceCapableDevice'
            );

            this.preferences['hasCaloriesAvailablePref'] = this.caloriesAvailablePref.get('value');

            this.preferences.showAllDayPulseOx = this.showAllDayPulseOx();
            this.preferences.showRespiration = this.showRespiration();
            this.preferences.showHydration = this.showHydration();
            this.preferences.showHealthSnapshot = this.showHealthSnapshot();
            this.preferences.showECG = showEcgInBackbone(this.ecgRolePreference, this.ecgEnabled, this.usageIndicators);

            let mctFeatureEnable = isMctFeatureEnabled({ userRoles: viewerSocialProfile.get('userRoles') });
            let userSettingsJson = this.userSettings.toJSON();
            let checkUserSettingsForPregnancy = true;
            if (this.pregnancysnapshots) {
                let today = new Date();
                if (isPregnancyDay(this.pregnancysnapshots.toJSON(), today)) {
                    this.preferences.showPregnancy = true;
                    checkUserSettingsForPregnancy = false;
                }
            }

            if (checkUserSettingsForPregnancy) {
                this.preferences.showPregnancy =
                    this.userSettings.getMenstrualCycleTrackingFeatureData() &&
                    this.userSettings.getMenstrualCycleTrackingFeatureData().menstrualCycleType === CYCLE_TYPE_PREGNANT;
            }

            this.preferences.showMenstrualCycleTracking =
                userSettingsJson &&
                userSettingsJson.userData &&
                showMenstrualCycleTrackingWithoutMCTSettingsData(mctFeatureEnable, userSettingsJson) &&
                !this.preferences.showPregnancy;

            this.preferences.showJetLag = this.showJetLag();

            this.preferences.showSteps = this.showSteps();
            this.preferences.showWheelchairPushes = this.showWheelchairPushes();
            this.preferences.showInsights = this.showInsights();

            this.preferences.hideFloorFeature = this.hideFloorFeature();

            this.preferences.isWheelchairMode = this.isWheelchairMode();

            this.preferences.showStressPage = this.showStressPage();
            // Clean up ticket: CA-161362
            this.preferences.dswEnabled = this.dswRole.featureEnabled();

            this.preferences.showHydrationPage = this.showHydrationPage(); // Cleanup ticket: CA-159279

            this.preferences.isEconomyEnabled = this.runningEconomy.featureEnabled(); // Cleanup ticket: CA-193107
            this.preferences.isToleranceEnabled = this.runningTolerance.featureEnabled(); // Clean up ticket: CA-185063
            this.preferences.isTrailEnabled = this.trail.getFeatureEnabledForMultipleRoles(); // Cleanup ticket: CA-159335

            this.sidebar = new ReactComponentView();
            this.sidebar.renderComponent(MainSidebar, jQuery('.main-nav')[0], {
                preferences: this.preferences,
                usageIndicators: this.usageIndicators,
                newsFeedStatus: this.newsFeedStatus,
                viewerSocialProfile: viewerSocialProfile.toJSON(),
                displayName: viewerSocialProfile.get('displayName'),
                navCollapsedPref: this.navCollapsedPref,
                setCollapsed: this.setCollapsed.bind(this)
            });

            this.setCollapsed(this.navCollapsedPref?.get('value') === 'true');

            if (!isRaceManagementMode()) {
                this.sidebar.element.classList.remove('hide');
            }
        }
    },

    setCollapsed: function(collapse, force = false) {
        const body = document.querySelector('body');
        const isMobile = body.classList.contains('mobile');
        const collapsedClass = isMobile ? 'mobile-open-main-nav' : 'minimize-main-nav';

        const wasCollapsed = body.classList.contains(collapsedClass);
        if (collapse === wasCollapsed && !force) return;

        if (collapse) {
            body.classList.add('main-nav-closed');
            body.classList.remove('main-nav-open');
            body.classList.add(collapsedClass);
            this.navCollapsedPref.set('value', 'true');
        } else {
            body.classList.add('main-nav-open');
            body.classList.remove('main-nav-closed');
            body.classList.remove(collapsedClass);
            this.navCollapsedPref.set('value', 'false');
        }

        this.navCollapsedPref.save();

        // Recalculate the width of iframe pages and Highcharts
        // > Highcharts is already setup to listen for a window resize event
        setTimeout(() => {
            window.dispatchEvent(new Event('resize.iframePage'));
            window.dispatchEvent(new Event('resize'));
        }, 250);
    },

    expandCollapse: function() {
        const body = document.querySelector('body');
        const isMobile = body.classList.contains('mobile');
        const collapsedClass = isMobile ? 'mobile-open-main-nav' : 'minimize-main-nav';

        this.setCollapsed(!body.classList.contains(collapsedClass), true);
    },

    renderHeaderBar: function() {
        this.topHeader = new TopHeaderBarView({
            el: jQuery('.main-body .header'),
            pollRate: QUEUE_POLL_RATE,
            userProfile: this.userProfile,
            userPreferences: this.userPreferences,
            messageAndDevicesNotificationsEnabled: this.messageAndDevicesNotificationsEnabled.get('value') === 'true',
            groupInviteSpammingDays: +this.groupInviteSpammingPref.get('value'),
            gcPlusFrameRole: this.gcPlusFrameRole, // Clean up ticket: CA-165763
            trainingPlans: this.trainingPlans
        });

        // Have to use the Backbone version of MainSidebarView here since the top header isn't React yet
        this.listenTo(this.topHeader, MainSidebarView.Events.EXPAND_COLLAPSE, this.expandCollapse.bind(this));

        this.topHeader.render();

        if (!isRaceManagementMode()) {
            this.topHeader.$el.removeClass('hide');
        }

        if (isReadonlyMode()) {
            this.renderSnackbarMessage();
            SnackbarMessageAction.display({ ns: 'settings', transKey: 'read_only_mode_alert_1' });
        }
    },

    renderCookieConsentBanner: function() {
        this.cookieConsentBanner = new ReactComponentView();
        this.cookieConsentBanner.renderComponent(Consent, jQuery('.main-footer')[0]);
    },

    setupReactModal: function() {
        this.modal = new ReactComponentView();
        this.modal.renderComponent(GlobalModal, jQuery('.react-global-modal')[0]);
    },

    showAllDayPulseOx: function() {
        return viewerSocialProfile.isWellnessUser();
    },

    showRespiration: function() {
        return viewerSocialProfile.isWellnessUser();
    },

    showHydration: function() {
        return viewerSocialProfile.isWellnessUser();
    },

    showHealthSnapshot: function() {
        return this.usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasHealthSnapshotCapableDevice');
    },

    showJetLag: function() {
        return this.usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasJetlagCapableDevice');
    },

    //Cleanup ticket: CA-135779
    showSteps: function() {
        /** https://confluence.garmin.com/pages/viewpage.action?spaceKey=DI&title=Web+-+Wheelchair+Pushes+Availability */
        /**
         * show when ONE condition match
         * - wheelchair mode OFF
         * - NO wheelchair device
         */
        const wheelchairModeEnabled = this.userSettings.getWheelchairModeEnabled();
        const hasWheelchairCapableDevice =
            this.usageIndicators &&
            this.usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasWheelchairCapableDevice');

        return !wheelchairModeEnabled || !hasWheelchairCapableDevice;
    },

    //Cleanup ticket: CA-135779
    showWheelchairPushes: function() {
        return (
            this.usageIndicators &&
            this.usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasWheelchairCapableDevice') &&
            this.userSettings.getWheelchairModeEnabled()
        );
    },

    showInsights: function() {
        const isWheelchairEnabled = this.userSettings.getWheelchairModeEnabled();
        const hasInsightsHistoryData = this.insightsHistory?.length > 0;

        if (isWheelchairEnabled) {
            return hasInsightsHistoryData;
        }

        return true;
    },

    showStressPage: function() {
        return this.gc5StressPageRole.featureEnabled();
    },

    showHydrationPage: function() {
        return this.gc5HydrationPageRole.featureEnabled(); // Cleanup ticket: CA-159279
    },

    hideFloorFeature: function() {
        if (!this.userSettings.getWheelchairModeEnabled()) {
            return false;
        }
        if (this.usageIndicators.has('ReportAvailability')) {
            return (
                !this.usageIndicators.get('ReportAvailability').hasFloorsAscended &&
                !this.usageIndicators.get('ReportAvailability').hasFloorsDescended
            );
        }
        return false;
    },

    reloadSidebar() {
        if (applyHarmonyConnectCustomization(this.harmonyConnectAppRole.featureEnabled())) {
            // skip the reload process as the sidebar is not rendered at all
            return;
        }
        let modelSynchronizer = new ModelSynchronizer();

        if (!isMale(this.userSettings.toJSON())) {
            this.pregnancysnapshots = new Pregnancysnapshots();
            modelSynchronizer.addModel({ model: this.pregnancysnapshots, required: true });
        }

        this.userSettings = new UserSettings({ useCache: false });
        modelSynchronizer.addModel({
            model: this.userSettings,
            required: true,
            countError: true
        });

        this.listenTo(modelSynchronizer, ModelSynchronizer.Events.SYNCHRONIZED, () => {
            this.forceReloadSidebar = true;
            this.renderSidebar();
        });

        modelSynchronizer.fetchModels();
    },

    isGdprFeatureOn: function(gdprFeature) {
        return GdprUtil.isFeatureForUserOn(
            gdprFeature.get('userOption'),
            gdprFeature.get('consentEnforcementState'),
            gdprFeature.get('sourceType')
        );
    },

    isWheelchairMode: function() {
        if (!this.userSettings.getWheelchairModeEnabled()) {
            return false;
        }
        return this.usageIndicators.checkDeviceIndicator('hasWheelchairCapableDevice');
    },

    logAppVersion: function() {
        const apiVersion = this.apiVersion.get('version');
        const gcVersion = window.VERSION;
        const urlBustValue = window.URL_BUST_VALUE;

        // Logging for QA and developers to see what Connect version
        // they are using, so do not translate
        console.log(`Version: ${gcVersion}, ${apiVersion}\nResources: ${urlBustValue}`);
    }
});

MainContainerView.Events = Events;
MainContainerView.QUEUE_POLL_RATE = QUEUE_POLL_RATE;

export default MainContainerView;
